import React, { useEffect, useRef, useState } from 'react';
import { Archive, ChevronDown, Eraser, FolderOpen, Minimize2, Save, Send, Ticket as TicketIcon, Trash2, X } from 'lucide-react';
import apiService from '../../services/apiService';
import { useI18n } from '../../utils/i18n';
import { useToast } from '../ToastProvider';
import { useConfirm } from '../ui/ConfirmDialog';
import MarkdownText from '../ui/MarkdownText';
import KayCeeKopf from '../header/KayCeeKopf';
import { useAssistent } from './AssistentProvider';
import './assistent.css';
// Ladepunkte (.ai-chat-dots) wie in der KI-Suche
import '../../styles/AiSearch.css';

// #512: Der Chat des KayCee-Assistenten, EINE Komponente für die Sprechblase am
// Arbeitsplatz und die Mobilansicht. Zustand und Verlauf kommen aus dem
// AssistentProvider, der Verlauf liegt auf dem Server (#513).
//
// Was das Modell gesucht hat, steht aufklappbar unter der Antwort (die
// Debug-Ansicht der alten Suche), die gefundenen Tickets als Karten darunter.

const FEHLERTEXT = {
  abgeschaltet: ['assistent.fehler.abgeschaltet', 'Die KI-Suche ist auf dieser Installation abgeschaltet.'],
  nichtEingerichtet: ['assistent.fehler.nichtEingerichtet', 'Es ist noch kein KI-Anbieter eingerichtet.'],
  laeuftNoch: ['assistent.fehler.laeuftNoch', 'Der Assistent arbeitet noch an einer Anfrage, vielleicht in einem anderen Fenster. Bitte gleich noch einmal versuchen.'],
  keineWerkzeuge: ['assistent.fehler.keineWerkzeuge', 'Das eingestellte KI-Modell kann keine Werkzeuge benutzen. Bitte im Admin-Bereich ein anderes Modell wählen.'],
  netz: ['assistent.fehler.netz', 'Keine Verbindung zum Server. Bitte noch einmal versuchen.'],
  allgemein: ['assistent.fehler.allgemein', 'Das hat nicht geklappt. Bitte noch einmal versuchen.']
};

const WERKZEUGNAME = {
  search: ['assistent.werkzeug.search', 'Suche'],
  get_ticket: ['assistent.werkzeug.getTicket', 'Ticket gelesen'],
  get_comments: ['assistent.werkzeug.getComments', 'Kommentare gelesen'],
  list_tickets: ['assistent.werkzeug.listTickets', 'Ticketliste'],
  planned_tickets: ['assistent.werkzeug.plannedTickets', 'Planung gelesen'],
  list_customers: ['assistent.werkzeug.listCustomers', 'Kundenliste'],
  whoami: ['assistent.werkzeug.whoami', 'Eigene Rechte geprüft']
};

function eingabeKurz(eingabe) {
  if (!eingabe || typeof eingabe !== 'object') return '';
  return Object.entries(eingabe)
    .filter(([, w]) => w !== undefined && w !== null && w !== '')
    .map(([k, w]) => (k === 'query' ? `„${w}“` : `${k}: ${typeof w === 'object' ? JSON.stringify(w) : w}`))
    .join(', ');
}

const Antwort = ({ eintrag, onOeffneTicket }) => {
  const { t } = useI18n();
  const schritte = Array.isArray(eintrag.schritte) ? eintrag.schritte : [];
  const karten = Array.isArray(eintrag.karten) ? eintrag.karten : [];
  return (
    <div className="assistent-blase antwort">
      {eintrag.content
        ? <MarkdownText>{eintrag.content}</MarkdownText>
        : <p className="assistent-leise">{t('assistent.keineAntwort', 'Keine Antwort erhalten.')}</p>}
      {eintrag.abbruch && (
        <p className="assistent-leise">{t('assistent.abbruch', 'Die Suche wurde an ihrer Grenze beendet; die Antwort stützt sich auf das bis dahin Gefundene.')}</p>
      )}
      {karten.length > 0 && (
        <div className="assistent-karten" aria-label={t('assistent.karten', 'Gefundene Tickets')}>
          {karten.map((k) => (
            <button key={`${k.ticketId}-${k.ticketNumber}`} type="button" className="assistent-karte" onClick={() => onOeffneTicket(k)}
              title={t('assistent.ticketOeffnen', 'Ticket öffnen')}>
              <TicketIcon size={14} aria-hidden="true" />
              <span className="assistent-karte-nummer">{k.ticketNumber}</span>
              <span className="assistent-karte-titel">{k.title}</span>
            </button>
          ))}
        </div>
      )}
      {schritte.length > 0 && (
        <details className="assistent-schritte">
          <summary>
            <ChevronDown size={14} aria-hidden="true" />
            {schritte.length === 1
              ? t('assistent.schritt', '1 Suchschritt')
              : t('assistent.schritte', '{{n}} Suchschritte', { n: schritte.length })}
          </summary>
          <ol>
            {schritte.map((s, i) => {
              const name = WERKZEUGNAME[s.werkzeug];
              return (
                <li key={i} className={s.ok ? '' : 'assistent-schritt--fehler'}>
                  {name ? t(name[0], name[1]) : s.werkzeug}
                  {eingabeKurz(s.eingabe) && <span className="assistent-leise"> {eingabeKurz(s.eingabe)}</span>}
                  {!s.ok && <span className="assistent-leise"> {t('assistent.schrittFehler', '(nicht möglich)')}</span>}
                </li>
              );
            })}
          </ol>
        </details>
      )}
    </div>
  );
};

const GespeicherteListe = ({ onSchliessen }) => {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();
  const { oeffneGespeichert, laeuft } = useAssistent();
  const [liste, setListe] = useState(null);

  useEffect(() => {
    let abgebrochen = false;
    apiService.listSearchConversations()
      .then((r) => { if (!abgebrochen) setListe(Array.isArray(r?.conversations) ? r.conversations : []); })
      .catch(() => { if (!abgebrochen) setListe([]); });
    return () => { abgebrochen = true; };
  }, []);

  const loeschen = async (eintrag) => {
    if (!(await confirm({ message: t('assistent.gespeichertLoeschenFrage', 'Gespeicherten Verlauf „{{titel}}“ löschen?', { titel: eintrag.title }), danger: true }))) return;
    try {
      await apiService.deleteSearchConversation(eintrag.id);
      setListe((l) => l.filter((x) => x.id !== eintrag.id));
    } catch (_) {
      pushToast({ type: 'error', message: t('assistent.fehler.allgemein', 'Das hat nicht geklappt. Bitte noch einmal versuchen.') });
    }
  };

  return (
    <div className="assistent-gespeichert">
      <div className="assistent-gespeichert-kopf">
        <strong>{t('assistent.gespeichert', 'Gespeicherte Verläufe')}</strong>
        <button type="button" className="assistent-knopf-klein" onClick={onSchliessen} aria-label={t('common.close', 'Schließen')} title={t('common.close', 'Schließen')}>
          <X size={16} />
        </button>
      </div>
      {liste === null && <p className="assistent-leise">{t('common.loading', 'Lädt…')}</p>}
      {liste && liste.length === 0 && <p className="assistent-leise">{t('assistent.gespeichertLeer', 'Noch nichts gespeichert.')}</p>}
      {liste && liste.length > 0 && (
        <ul>
          {liste.map((g) => (
            <li key={g.id}>
              <button type="button" className="assistent-gespeichert-titel" disabled={laeuft}
                onClick={async () => { if (await oeffneGespeichert(g.id)) onSchliessen(); }}>
                {g.title}
              </button>
              <button type="button" className="assistent-knopf-klein" onClick={() => loeschen(g)}
                aria-label={t('assistent.gespeichertLoeschen', 'Gespeicherten Verlauf löschen')} title={t('assistent.gespeichertLoeschen', 'Gespeicherten Verlauf löschen')}>
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AssistentChat = ({ onClose, onOeffneTicket, mobil = false }) => {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();
  const { anzeige, stand, laeuft, fehler, setFehler, laden, senden, zusammenfassen, leeren, speichern } = useAssistent();
  const [text, setText] = useState('');
  const [gespeichertOffen, setGespeichertOffen] = useState(false);
  const endeRef = useRef(null);
  const feldRef = useRef(null);

  // Beim Öffnen den Stand vom Server holen (zweiter Tab, anderes Gerät, nachts geleert)
  useEffect(() => { laden(); }, [laden]);

  useEffect(() => {
    try { endeRef.current?.scrollIntoView({ block: 'end' }); } catch (_) {
      // scrollIntoView fehlt nur in sehr alten Browsern
    }
  }, [anzeige, laeuft]);

  const absenden = async (e) => {
    e.preventDefault();
    const frage = text.trim();
    if (!frage || laeuft) return;
    setText('');
    const ok = await senden(frage);
    if (!ok) setText(frage); // nichts verloren: die Frage steht wieder im Feld
    try { feldRef.current?.focus(); } catch (_) {
      // Fokus ist nur Komfort
    }
  };

  const fragenUndLeeren = async () => {
    if (!(await confirm({ message: t('assistent.leerenFrage', 'Den laufenden Verlauf leeren? Gespeicherte Verläufe bleiben.'), danger: true }))) return;
    leeren();
  };

  const speichernMitMeldung = async () => {
    if (await speichern()) pushToast({ type: 'success', message: t('assistent.gespeichertOk', 'Verlauf gespeichert.') });
  };

  const abgeschaltet = fehler === 'abgeschaltet';
  const hatVerlauf = anzeige.length > 0;

  return (
    <div className={`assistent-chat${mobil ? ' assistent-chat--mobil' : ''}`} role="dialog" aria-label={t('assistent.titel', 'KayCee-Assistent')}>
      <header className="assistent-kopf">
        <KayCeeKopf size={22} />
        <h2>{t('assistent.titel', 'KayCee-Assistent')}</h2>
        <div className="assistent-kopf-aktionen">
          <button type="button" className="assistent-knopf-klein" onClick={() => setGespeichertOffen((o) => !o)}
            aria-label={t('assistent.gespeichert', 'Gespeicherte Verläufe')} title={t('assistent.gespeichert', 'Gespeicherte Verläufe')}>
            <FolderOpen size={16} />
          </button>
          <button type="button" className="assistent-knopf-klein" onClick={speichernMitMeldung} disabled={!hatVerlauf || laeuft}
            aria-label={t('assistent.speichern', 'Verlauf speichern')} title={t('assistent.speichern', 'Verlauf speichern')}>
            <Save size={16} />
          </button>
          <button type="button" className="assistent-knopf-klein" onClick={zusammenfassen} disabled={!hatVerlauf || laeuft}
            aria-label={t('assistent.zusammenfassen', 'Verlauf zusammenfassen')} title={t('assistent.zusammenfassen', 'Verlauf zusammenfassen')}>
            <Archive size={16} />
          </button>
          <button type="button" className="assistent-knopf-klein" onClick={fragenUndLeeren} disabled={!hatVerlauf || laeuft}
            aria-label={t('assistent.leeren', 'Verlauf leeren')} title={t('assistent.leeren', 'Verlauf leeren')}>
            <Eraser size={16} />
          </button>
          <button type="button" className="assistent-knopf-klein" onClick={onClose}
            aria-label={mobil ? t('mobile.back', 'Zurück') : t('assistent.schliessen', 'Assistent schließen')}
            title={mobil ? t('mobile.back', 'Zurück') : t('assistent.schliessen', 'Assistent schließen')}>
            {mobil ? <X size={18} /> : <Minimize2 size={16} />}
          </button>
        </div>
      </header>

      {gespeichertOffen && <GespeicherteListe onSchliessen={() => setGespeichertOffen(false)} />}

      {stand.hinweis && (
        <div className="assistent-hinweis" role="note">
          <p>{t('assistent.hinweisGross', 'Der Verlauf ist groß geworden. Jede weitere Frage wird dadurch langsamer und teurer. Bitte zusammenfassen oder leeren.')}</p>
          <div className="assistent-hinweis-knoepfe">
            <button type="button" className="btn btn-secondary btn-sm" onClick={zusammenfassen} disabled={laeuft}>{t('assistent.zusammenfassen', 'Verlauf zusammenfassen')}</button>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={fragenUndLeeren} disabled={laeuft}>{t('assistent.leeren', 'Verlauf leeren')}</button>
          </div>
        </div>
      )}

      <div className="assistent-verlauf" aria-live="polite">
        {!hatVerlauf && !laeuft && !abgeschaltet && (
          <p className="assistent-leer">{t('assistent.leerText', 'Frag mich etwas zum Ticketbestand, z. B. „Gab es den Druckerfehler bei Kunde X schon einmal?“ Ich suche selbst, auch mehrmals, und nenne die Tickets dazu.')}</p>
        )}
        {anzeige.map((e, i) => {
          if (e.role === 'user') return <div key={i} className="assistent-blase frage">{e.content}</div>;
          if (e.role === 'assistant') return <Antwort key={i} eintrag={e} onOeffneTicket={onOeffneTicket} />;
          if (e.role === 'hinweis' && e.art === 'zusammengefasst') {
            return (
              <p key={i} className="assistent-vermerk">
                {e.automatisch
                  ? t('assistent.zusammengefasstAuto', 'Der Verlauf wurde automatisch zusammengefasst, weil er sehr groß geworden war.')
                  : t('assistent.zusammengefasst', 'Der Verlauf wurde zusammengefasst.')}
              </p>
            );
          }
          return null;
        })}
        {laeuft && (
          <div className="assistent-blase antwort" role="status" aria-label={t('assistent.sucht', 'Der Assistent sucht…')}>
            <div className="ai-chat-dots" aria-hidden="true">
              <span className="ai-chat-dot" /><span className="ai-chat-dot" /><span className="ai-chat-dot" />
            </div>
          </div>
        )}
        {fehler && (
          <p className="assistent-fehler" role="alert">
            {t(...(FEHLERTEXT[fehler] || FEHLERTEXT.allgemein))}
            {!abgeschaltet && (
              <button type="button" className="assistent-knopf-klein" onClick={() => setFehler(null)} aria-label={t('common.close', 'Schließen')} title={t('common.close', 'Schließen')}>
                <X size={14} />
              </button>
            )}
          </p>
        )}
        <div ref={endeRef} />
      </div>

      {!abgeschaltet && (
        <form className="assistent-eingabe" onSubmit={absenden}>
          <textarea
            ref={feldRef}
            rows={2}
            value={text}
            maxLength={4000}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) absenden(e); }}
            placeholder={hatVerlauf ? t('assistent.platzhalterRueckfrage', 'Rückfrage stellen…') : t('assistent.platzhalter', 'Frage an den Assistenten…')}
            aria-label={t('assistent.frage', 'Frage an den Assistenten')}
            enterKeyHint="send"
          />
          <button type="submit" className="assistent-senden" disabled={laeuft || !text.trim()}
            aria-label={t('assistent.senden', 'Senden')} title={t('assistent.senden', 'Senden')}>
            <Send size={18} />
          </button>
        </form>
      )}
    </div>
  );
};

export default AssistentChat;
